import React from "react";
import toast from "react-hot-toast";

export interface UseLoadingToastOptions {
  loadingMessage?: string;
  successMessage?: string;
  errorMessage?: string;
}

export interface UseLoadingToastReturn {
  startLoading: (
    message?: Parameters<typeof toast.loading>[0],
    options?: Parameters<typeof toast.loading>[1]
  ) => void;
  dismiss: () => void;
  error: (
    message?: Parameters<typeof toast.error>[0],
    options?: Parameters<typeof toast.error>[1]
  ) => void;
  success: (
    message?: Parameters<typeof toast.success>[0],
    options?: Parameters<typeof toast.success>[1]
  ) => void;
}

export const useLoadingToast = (
  options?: UseLoadingToastOptions
): UseLoadingToastReturn => {
  const toastId = React.useRef<string | undefined>(undefined);
  const {
    loadingMessage = "",
    successMessage = "",
    errorMessage = "Something went wrong"
  } = options || {};

  const startLoading: UseLoadingToastReturn["startLoading"] = (
    message,
    options
  ) => {
    toastId.current = toast.loading(message || loadingMessage, {
      duration: Number.POSITIVE_INFINITY,
      ...options
    });
  };

  const dismiss = () => {
    if (!toastId.current) return;

    toast.dismiss(toastId.current);
  };

  const error: UseLoadingToastReturn["error"] = (message, options) => {
    toast.error(
      message || errorMessage,
      toastId.current || options
        ? {
            duration: 0,
            id: toastId.current,
            ...options
          }
        : undefined
    );
  };

  const success: UseLoadingToastReturn["success"] = (message, options) => {
    toast.success(
      message || successMessage,
      toastId.current || options
        ? {
            duration: 0,
            id: toastId.current,
            ...options
          }
        : undefined
    );
  };

  return { startLoading, dismiss, error, success };
};
